import React, { useEffect, useRef } from 'react';
import { usePlayer } from '../contexts/PlayerContext';
import { useTheme } from '../contexts/ThemeContext';
import { hexToRgb } from '../utils/color';
interface Particle {
  x: number;
  y: number;
  r: number;
  vx: number;
  vy: number;
  hue: number;
  phase: number;
}

/**
 * Dust in the projector beam: a light canvas particle field that drifts slowly
 * on its own and lifts with the live audio level. Decorative, so it stays out
 * of the accessibility tree and stops entirely for reduced-motion users.
 */
export function ParticleField() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const {
    getLevel,
    isPlaying
  } = usePlayer();
  const {
    accent,
    secondary
  } = useTheme();
  const levelFn = useRef(getLevel);
  const playing = useRef(isPlaying);
  levelFn.current = getLevel;
  playing.current = isPlaying;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const accentRgb = hexToRgb(accent);
    const secondaryRgb = hexToRgb(secondary);
    const dpr = Math.min(window.devicePixelRatio, 2);
    let particles: Particle[] = [];
    let width = 0;
    let height = 0;
    const build = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(Math.min(120, Math.max(36, width * height / 16000)));
      particles = Array.from({
        length: count
      }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: 0.6 + Math.random() * 1.9,
        vx: (Math.random() - 0.5) * 0.09,
        vy: -0.05 - Math.random() * 0.14,
        hue: Math.random(),
        phase: Math.random() * Math.PI * 2
      }));
    };
    build();
    const observer = new ResizeObserver(build);
    observer.observe(canvas);
    let frame = 0;
    let smoothed = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      const raw = playing.current ? levelFn.current() : 0;
      smoothed += (raw - smoothed) * 0.07;
      const t = performance.now() / 1000;
      ctx.clearRect(0, 0, width, height);
      for (const p of particles) {
        p.x += p.vx + Math.sin(t * 0.4 + p.phase) * 0.12;
        p.y += p.vy * (1 + smoothed * 3.2);
        if (p.y < -8) {
          p.y = height + 8;
          p.x = Math.random() * width;
        }
        if (p.x < -8) p.x = width + 8;
        if (p.x > width + 8) p.x = -8;
        const [r, g, b] = p.hue > 0.5 ? accentRgb : secondaryRgb;
        const alpha = 0.1 + p.hue * 0.18 + smoothed * 0.4;
        const radius = p.r * (1 + smoothed * 0.7);
        ctx.beginPath();
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${Math.min(0.7, alpha)})`;
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    tick();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [accent, secondary]);
  return <canvas ref={canvasRef} aria-hidden="true" className="absolute inset-0 h-full w-full" data-id="element-67" data-mp="1846y8z142cdv" />;
}